import React from 'react';
import { Phone, MapPin, Calendar, Award } from 'lucide-react';
import { Button } from './ui/button';
import { campInfo } from '../data/mock';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-emerald-100">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              {/* Main Heading */}
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <div className="inline-flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full">
                    <Award className="w-5 h-5 text-emerald-700" />
                    <span className="text-sm font-semibold text-emerald-700">Od roku {campInfo.founded}</span>
                  </div>
                  <div className="inline-flex items-center space-x-2 bg-amber-100 px-4 py-2 rounded-full">
                    <Calendar className="w-5 h-5 text-amber-700" />
                    <span className="text-sm font-semibold text-amber-700">Sezóna začíná 15.5.2026</span>
                  </div>
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                  Kemp Josef
                </h1>
                <p className="text-2xl md:text-3xl text-emerald-600 font-semibold">
                  Rodinný kemp v srdci jižní Moravy
                </p>
              </div>

              {/* Description */}
              <p className="text-lg text-gray-700 leading-relaxed">
                {campInfo.description}
              </p>

              {/* Info Stats - Modern Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow">
                  <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
                  <p className="text-sm text-gray-600 mb-1">Lokalita</p>
                  <p className="font-semibold text-gray-900">Dubňany</p>
                  <p className="text-xs text-gray-500">jižní Morava</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow">
                  <Calendar className="w-8 h-8 text-emerald-600 mb-3" />
                  <p className="text-sm text-gray-600 mb-1">Služba</p>
                  <p className="font-semibold text-gray-900">24/7</p>
                  <p className="text-xs text-gray-500">Vždy k dispozici</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href="tel:+420721022279" className="flex-1 sm:flex-initial">
                  <Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 rounded-xl">
                    <Phone className="w-5 h-5 mr-2" />
                    Rezervovat nyní
                  </Button>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                  className="flex-1 sm:flex-initial border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-lg px-8 py-6 rounded-xl transition-all duration-300 hover:scale-105"
                >
                  Zjistit více
                </Button>
              </div>
            </div>

            {/* Right Column - Hero Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://i.ibb.co/p6mT366r/ee36b6e1-6d6a-4b45-a3bd-feb7987d5c46.jpg"
                  alt="Kemp Josef - Akce a oslavy"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent"></div>
                
                {/* Floating Info Card on Image */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Pořádáme akce na míru</p>
                      <p className="text-lg font-bold text-gray-900">Svatby, oslavy & večírky</p>
                    </div>
                    <div className="bg-emerald-100 p-3 rounded-full">
                      <Award className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-emerald-300 rounded-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-emerald-600 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-emerald-600 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
